import { useEffect, useMemo, useRef, useState } from 'react'
import type { AssessmentTimeLimitConfig } from '@really-assess/shared/proctoring'

export function buildTimeWarningThresholds(config: AssessmentTimeLimitConfig): number[] {
  if (!config.enabled) return []

  const { durationMs, firstWarningAtRemainingMs, warningIntervalMs } = config
  if (firstWarningAtRemainingMs <= 0 || firstWarningAtRemainingMs >= durationMs) {
    return []
  }

  const thresholds = [firstWarningAtRemainingMs]
  if (warningIntervalMs > 0) {
    let next = firstWarningAtRemainingMs - warningIntervalMs
    while (next > 0) {
      thresholds.push(next)
      next -= warningIntervalMs
    }
  }

  return thresholds.sort((a, b) => b - a)
}

export function formatRemainingMs(ms: number): string {
  const totalSec = Math.max(0, Math.ceil(ms / 1000))
  const mins = Math.floor(totalSec / 60)
  const secs = totalSec % 60

  if (mins >= 60) {
    const hours = Math.floor(mins / 60)
    const remMins = mins % 60
    if (remMins > 0) {
      return `${hours} hour${hours === 1 ? '' : 's'} and ${remMins} minute${remMins === 1 ? '' : 's'}`
    }
    return `${hours} hour${hours === 1 ? '' : 's'}`
  }

  if (mins > 0 && secs > 0) {
    return `${mins} minute${mins === 1 ? '' : 's'} and ${secs} second${secs === 1 ? '' : 's'}`
  }
  if (mins > 0) {
    return `${mins} minute${mins === 1 ? '' : 's'}`
  }
  return `${secs} second${secs === 1 ? '' : 's'}`
}

export function formatCountdown(ms: number): string {
  const totalSec = Math.max(0, Math.ceil(ms / 1000))
  const hours = Math.floor(totalSec / 3600)
  const mins = Math.floor((totalSec % 3600) / 60)
  const secs = totalSec % 60

  if (hours > 0) {
    return `${hours}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
  }
  return `${mins}:${String(secs).padStart(2, '0')}`
}

interface UseAssessmentTimeLimitOptions {
  config: AssessmentTimeLimitConfig
  startedAt: number | null
  active: boolean
  onWarning: (remainingMs: number) => void
  onTimeout: () => void
}

export function useAssessmentTimeLimit({
  config,
  startedAt,
  active,
  onWarning,
  onTimeout,
}: UseAssessmentTimeLimitOptions) {
  const [remainingMs, setRemainingMs] = useState<number | null>(null)
  const firedWarningsRef = useRef<Set<number>>(new Set())
  const timeoutHandledRef = useRef(false)
  const warningThresholds = useMemo(() => buildTimeWarningThresholds(config), [config])

  useEffect(() => {
    firedWarningsRef.current = new Set()
    timeoutHandledRef.current = false
    setRemainingMs(null)
  }, [startedAt, config.enabled, config.durationMs])

  useEffect(() => {
    if (!config.enabled || !startedAt || !active) {
      setRemainingMs(null)
      return
    }

    const tick = () => {
      const elapsed = Date.now() - startedAt
      const remaining = config.durationMs - elapsed
      setRemainingMs(remaining)

      if (remaining <= 0) {
        if (!timeoutHandledRef.current) {
          timeoutHandledRef.current = true
          onTimeout()
        }
        return
      }

      for (const threshold of warningThresholds) {
        if (remaining <= threshold && !firedWarningsRef.current.has(threshold)) {
          firedWarningsRef.current.add(threshold)
          onWarning(remaining)
        }
      }
    }

    tick()
    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
  }, [
    active,
    config.durationMs,
    config.enabled,
    onTimeout,
    onWarning,
    startedAt,
    warningThresholds,
  ])

  return {
    remainingMs,
    countdownLabel: remainingMs === null ? null : formatCountdown(remainingMs),
    isExpired: remainingMs !== null && remainingMs <= 0,
  }
}
